import isNumber from 'lodash/isNumber';

import type { PayV3Histogram, PayV3Overtime } from '@revelio/data-access';

import type { CompensationChartType } from '../../PageConfigProvider/types/chart';
import type { CompensationSecondaryDimension } from '../../PageConfigProvider/types/dimension';
import {
  isPayPercentileMetric,
  type PayMetricValues,
} from '../../PageConfigProvider/types/metric';
import type { CompensationData } from '../compensation/CompensationDataProvider';
import {
  type BoxDataSet,
  type BreakdownBoxDataSet,
  type BreakdownSingleDataSet,
  DataType,
  type DistributionData,
  type DistributionDataPoint,
  type PercentileDataPoint,
  type SingleDataPoint,
  type SingleDataSet,
  type TimeseriesData,
  type TimeseriesDataPoint,
} from '../types';

/**
 * Loose pay_v3 response shape accepted by the converter: the union of the
 * full, tooltip and top-geos selections (metrics fields optional), so every
 * pay document's result — and the normalized v2 responses — convert through
 * one function.
 */
export type PayResponseEntity = {
  id: number;
  short_name: string;
  long_name: string;
  metrics: {
    overtime?: PayV3Overtime;
    histogram?: PayV3Histogram;
    top_companies?: PayV3Histogram[];
    top_geos?: PayV3Histogram[];
    top_roles?: PayV3Histogram[];
  };
};

export type PayResponse = {
  pay_v3?: {
    entities: PayResponseEntity[];
    refs: { months: string[] };
  } | null;
};

// Constants
const METRIC_BUCKET_SIZES: Record<PayMetricValues, number> = {
  total_compensation: 1000,
  base_salary: 1000,
  contractor_pay_rate: 1,
  total_spend_salary: 1000,
  total_spend_compensation: 1000,
  base_salary_percentiles: 1000,
  total_compensation_percentiles: 1000,
  contractor_pay_rate_percentiles: 1,
} as const;

type PayMetricFields = keyof Pick<PayV3Histogram, 'base' | 'total' | 'hourly'>;
const METRIC_FIELD_MAP: Record<PayMetricValues, PayMetricFields> = {
  total_compensation: 'total',
  base_salary: 'base',
  contractor_pay_rate: 'hourly',
  total_spend_salary: 'base',
  total_spend_compensation: 'total',
  base_salary_percentiles: 'base',
  total_compensation_percentiles: 'total',
  contractor_pay_rate_percentiles: 'hourly',
} as const;

// Helper to fetch top data list based on selected secondary dimension
const selectEntityTopData = (
  entity: PayResponseEntity,
  secondaryDimension?: CompensationSecondaryDimension
) => {
  switch (secondaryDimension) {
    case 'company':
      return entity.metrics.top_companies ?? [];
    case 'geography':
      return entity.metrics.top_geos ?? [];
    case 'role':
      return entity.metrics.top_roles ?? [];
    default:
      return [];
  }
};

// Compute p10/p50/p90 from entity histogram (bucket counts)
// The pay API only returns percentiles for breakdown segments, not for query entities
type HistogramPercentileKey = 'p10' | 'p25' | 'p50' | 'p75' | 'p90';

const HISTOGRAM_PERCENTILE_THRESHOLDS: Record<HistogramPercentileKey, number> =
  {
    p10: 0.1,
    p25: 0.25,
    p50: 0.5,
    p75: 0.75,
    p90: 0.9,
  };

const getPercentilesFromHistogramByKeys = (
  histogramCounts: Array<number | undefined>,
  bucketSize: number,
  exchangeRate: number,
  keys: HistogramPercentileKey[]
): Partial<Record<HistogramPercentileKey, number>> | null => {
  const counts = histogramCounts.map((c) => c ?? 0);
  const total = counts.reduce((s, c) => s + c, 0);
  if (total === 0) return null;

  const uniqueKeys = Array.from(new Set(keys));
  const targets = uniqueKeys
    .map((key) => ({
      key,
      threshold: HISTOGRAM_PERCENTILE_THRESHOLDS[key],
    }))
    .filter((t): t is { key: HistogramPercentileKey; threshold: number } =>
      Boolean(t.threshold)
    )
    .sort((a, b) => a.threshold - b.threshold);

  const percentiles: Partial<Record<HistogramPercentileKey, number>> = {};

  let cum = 0;
  let targetIdx = 0;

  for (let i = 0; i < counts.length && targetIdx < targets.length; i++) {
    cum += counts[i] ?? 0;
    const cumProb = cum / total;

    while (targetIdx < targets.length) {
      const target = targets[targetIdx];
      if (!target || cumProb < target.threshold) break;

      percentiles[target.key] = (i + 0.5) * bucketSize * exchangeRate;
      targetIdx++;
    }
  }

  for (let j = targetIdx; j < targets.length; j++) {
    const target = targets[j];
    if (target) {
      percentiles[target.key] =
        (counts.length - 0.5) * bucketSize * exchangeRate;
    }
  }

  return percentiles;
};

export const getPooledPercentilesFromHistogram = (
  histogramCounts: Array<number | undefined>,
  bucketSize: number,
  exchangeRate: number
): PercentileDataPoint | null => {
  const result = getPercentilesFromHistogramByKeys(
    histogramCounts,
    bucketSize,
    exchangeRate,
    ['p10', 'p25', 'p50', 'p75', 'p90']
  );
  if (
    result?.p10 == null ||
    result?.p25 == null ||
    result?.p50 == null ||
    result?.p75 == null ||
    result?.p90 == null
  ) {
    return null;
  }
  return result as PercentileDataPoint;
};

const getPercentilesFromHistogram = (
  histogramCounts: Array<number | undefined>,
  bucketSize: number,
  exchangeRate: number
): {
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
} | null => {
  const p = getPercentilesFromHistogramByKeys(
    histogramCounts,
    bucketSize,
    exchangeRate,
    ['p10', 'p25', 'p50', 'p75', 'p90']
  );
  if (!p) return null;
  const { p10, p25, p50, p75, p90 } = p;
  if (p10 == null || p25 == null || p50 == null || p75 == null || p90 == null) {
    return null;
  }
  return { p10, p25, p50, p75, p90 };
};

export const getDistributionDataFromPay = (
  data: PayResponseEntity[],
  metric: PayMetricValues,
  exchangeRate = 1
): DistributionData[] => {
  if (!data) return [];

  const bucketSize = METRIC_BUCKET_SIZES[metric];
  const fieldName = METRIC_FIELD_MAP[metric] || metric;

  return data
    .map((entity): DistributionData | null => {
      const dim = entity?.long_name;
      const id = String(entity.id);
      const histogramData = entity?.metrics?.histogram?.[fieldName];

      if (!dim || !histogramData || histogramData.length === 0) {
        return null;
      }

      const totalCount = histogramData.reduce(
        (sum, count) => sum + (count || 0),
        0
      );
      if (totalCount === 0) return null;

      let cumulativeCount = 0;
      const distribution: DistributionDataPoint[] = histogramData.map(
        (count, index) => {
          const validCount = count || 0;
          cumulativeCount += validCount;

          return {
            value: index * bucketSize * exchangeRate,
            kde: validCount,
            cdf: cumulativeCount / totalCount,
          };
        }
      );

      const percentileData = getPooledPercentilesFromHistogram(
        histogramData,
        bucketSize,
        exchangeRate
      );
      return {
        ...(id ? { id } : {}),
        dim,
        distribution,
        ...(percentileData && {
          percentiles: {
            p25: percentileData.p25,
            p50: percentileData.p50,
            p75: percentileData.p75,
          },
        }),
      };
    })
    .filter((item): item is DistributionData => item !== null);
};

export const convertPayData = ({
  data,
  secondaryDimension,
  metric,
  chartType,
  exchangeRate = 1,
}: {
  data: PayResponse;
  secondaryDimension?: CompensationSecondaryDimension;
  metric: PayMetricValues;
  chartType: CompensationChartType;
  exchangeRate?: number;
}): CompensationData => {
  const getEntityLabel = (entity: PayResponseEntity): string | null => {
    return entity.short_name || entity.long_name || null;
  };

  if (chartType === 'text') {
    const entities = data.pay_v3?.entities ?? [];
    const fieldName = METRIC_FIELD_MAP[metric] || metric;

    const breakdownMap: Record<string, BoxDataSet[]> = {};

    entities.forEach((entity) => {
      const entityLabel = getEntityLabel(entity);
      if (!entityLabel) return;

      const topData = [
        ...(entity.metrics?.top_companies ?? []),
        ...(entity.metrics?.top_geos ?? []),
        ...(entity.metrics?.top_roles ?? []),
      ];

      topData.forEach((segmentItem) => {
        const segmentLabel = segmentItem.short_name || segmentItem.long_name;
        if (!segmentLabel) return;

        const values = segmentItem[fieldName] || [];
        if (!Array.isArray(values) || values.length < 3) return;

        const p10Raw = values[0];
        const p50Raw = values[1];
        const p90Raw = values[2];
        if (
          typeof p10Raw !== 'number' ||
          typeof p50Raw !== 'number' ||
          typeof p90Raw !== 'number'
        ) {
          return;
        }

        breakdownMap[segmentLabel] = [
          ...(breakdownMap[segmentLabel] || []),
          {
            dim: entityLabel,
            box: {
              q10: p10Raw * (exchangeRate ?? 1),
              q50: p50Raw * (exchangeRate ?? 1),
              q90: p90Raw * (exchangeRate ?? 1),
            },
          },
        ];
      });
    });

    const breakdownBoxData = Object.entries(breakdownMap).map(
      ([dim, breakdown]) => ({
        dim,
        breakdown,
      })
    );

    if (breakdownBoxData.length > 0) {
      return { type: DataType.BOX, data: breakdownBoxData };
    }
  }

  if (chartType === 'line') {
    const timeseriesData: TimeseriesData[] = (() => {
      if (!data.pay_v3?.entities) return [];

      return data.pay_v3.entities
        .map((entity): TimeseriesData | null => {
          const overtimeData = entity.metrics.overtime;
          const values = (() => {
            switch (metric) {
              case 'base_salary':
              case 'base_salary_percentiles':
                return overtimeData?.salary_base || [];
              case 'total_compensation':
              case 'total_compensation_percentiles':
                return overtimeData?.salary_total || [];
              case 'contractor_pay_rate':
              case 'contractor_pay_rate_percentiles':
                return overtimeData?.salary_hourly || [];
              case 'total_spend_salary':
                return overtimeData?.salary_base || [];
              case 'total_spend_compensation':
                return overtimeData?.salary_total || [];
              default:
                return [];
            }
          })();
          const counts = overtimeData?.counts || [];
          const entityId = entity.id;
          const months = data.pay_v3?.refs.months;
          if (
            !entityId ||
            !Array.isArray(values) ||
            !Array.isArray(months) ||
            months.length !== values.length
          ) {
            return null;
          }
          const dim =
            entity.short_name ||
            entity.long_name ||
            String(entityId) ||
            'Unknown';
          const lineValues = months.reduce<TimeseriesDataPoint[]>(
            (acc, dateStr, idx) => {
              const val = values[idx];
              const count = counts[idx];
              const totalValue = (() => {
                if (
                  metric === 'total_spend_salary' ||
                  metric === 'total_spend_compensation'
                ) {
                  if (!count || !val) return null;
                  return count * val;
                }
                return val;
              })();
              const date = new Date(dateStr);
              // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
              if (!isNaN(date.getTime())) {
                const numericValue = isNumber(totalValue)
                  ? totalValue * exchangeRate
                  : null;
                acc.push({
                  date: date.toISOString().split('T')?.[0] ?? '',
                  value: numericValue,
                });
              }
              return acc;
            },
            []
          );
          if (lineValues.length === 0) return null;
          return {
            id: String(entity.id),
            dim,
            timeseries: lineValues,
          };
        })
        .filter((ld) => ld !== null);
    })();

    return { type: DataType.TIMESERIES, data: timeseriesData };
  }

  if (chartType === 'box') {
    const breakdownBoxData: BreakdownBoxDataSet['data'] = (() => {
      if (!data.pay_v3?.entities) return [];

      // No secondary dimension: one box per entity using histogram percentiles
      if (!secondaryDimension) {
        const bucketSize = METRIC_BUCKET_SIZES[metric];
        const fieldName = METRIC_FIELD_MAP[metric] || metric;
        const result: BreakdownBoxDataSet['data'] = [];

        for (const entity of data.pay_v3.entities) {
          const primaryDimLabel = entity.short_name || entity.long_name;
          if (!primaryDimLabel) continue;

          const histogramField = entity.metrics?.histogram?.[fieldName];
          const counts = Array.isArray(histogramField) ? histogramField : [];
          const percentiles = getPercentilesFromHistogram(
            counts,
            bucketSize,
            exchangeRate
          );
          if (!percentiles) continue;

          const entityId = String(entity.id);
          result.push({
            dim: primaryDimLabel,
            breakdown: [
              {
                dim: primaryDimLabel,
                ...(entityId && { id: entityId }),
                box: {
                  q10: percentiles.p10,
                  q25: percentiles.p25,
                  q50: percentiles.p50,
                  q75: percentiles.p75,
                  q90: percentiles.p90,
                },
              },
            ],
          });
        }

        return result.sort(
          (a, b) =>
            (b.breakdown[0]?.box.q50 ?? 0) - (a.breakdown[0]?.box.q50 ?? 0)
        );
      }

      // With secondary dimension: existing breakdown by top_* data
      const breakdownMap: Record<string, BoxDataSet[]> =
        data.pay_v3.entities.reduce<Record<string, BoxDataSet[]>>(
          (acc, entity) => {
            const primaryDimLabel = entity.short_name || entity.long_name;
            if (!primaryDimLabel) return acc;

            const topData = selectEntityTopData(entity, secondaryDimension);

            topData.forEach((item) => {
              const secondaryDimLabel = item.short_name || item.long_name;
              if (!secondaryDimLabel) return;

              const fieldName = METRIC_FIELD_MAP[metric] || metric;

              const values = item[fieldName] || [];

              const p10Raw = values[0];
              const p50Raw = values[1];
              const p90Raw = values[2];

              const boxData = {
                p10:
                  typeof p10Raw === 'number' ? p10Raw * exchangeRate : p10Raw,
                p50:
                  typeof p50Raw === 'number' ? p50Raw * exchangeRate : p50Raw,
                p90:
                  typeof p90Raw === 'number' ? p90Raw * exchangeRate : p90Raw,
              };

              const entityId = String(entity.id);
              acc[secondaryDimLabel] = [
                // @ts-expect-error noUncheckedIndexedAccess
                ...(acc[secondaryDimLabel] || []),
                {
                  dim: primaryDimLabel,
                  ...(entityId && { id: entityId }),
                  // @ts-expect-error noUncheckedIndexedAccess
                  box: { q10: boxData.p10, q50: boxData.p50, q90: boxData.p90 },
                },
              ];
            });

            return acc;
          },
          {}
        );

      const mapped = Object.entries(breakdownMap).map(([dim, boxData]) => ({
        dim,
        breakdown: boxData,
      }));

      return mapped.sort(
        (a, b) =>
          b.breakdown.reduce((acc, val) => acc + val.box.q50, 0) -
          a.breakdown.reduce((acc, val) => acc + val.box.q50, 0)
      );
    })();

    return { type: DataType.BOX, data: breakdownBoxData };
  }

  if (chartType === 'distribution') {
    const distributionData = getDistributionDataFromPay(
      data.pay_v3?.entities ?? [],
      metric,
      exchangeRate
    );
    return { type: DataType.DISTRIBUTION, data: distributionData };
  }

  // Percentile metrics: composite p10/p25/p50/p75/p90 from histogram
  if (
    chartType === 'table' &&
    !secondaryDimension &&
    isPayPercentileMetric(metric)
  ) {
    const entities = data.pay_v3?.entities ?? [];
    const fieldName = METRIC_FIELD_MAP[metric];
    const bucketSize = METRIC_BUCKET_SIZES[metric];

    type PercentilePointWithHistogram = SingleDataPoint &
      PercentileDataPoint & {
        histogramCounts: number[];
        bucketSize: number;
        exchangeRate: number;
      };
    const points: PercentilePointWithHistogram[] = entities
      .map((entity): PercentilePointWithHistogram | null => {
        const dim = entity.short_name || entity.long_name || null;
        if (!dim) return null;

        const histogramField = entity.metrics?.histogram?.[fieldName];
        const counts = Array.isArray(histogramField) ? histogramField : [];
        const percentiles = getPercentilesFromHistogramByKeys(
          counts,
          bucketSize,
          exchangeRate,
          ['p10', 'p25', 'p50', 'p75', 'p90']
        );

        if (
          percentiles?.p10 == null ||
          percentiles?.p25 == null ||
          percentiles?.p50 == null ||
          percentiles?.p75 == null ||
          percentiles?.p90 == null
        ) {
          return null;
        }

        const id = String(entity.id);

        return {
          ...(id && { id }),
          dim,
          value: percentiles.p50,
          p10: percentiles.p10,
          p25: percentiles.p25,
          p50: percentiles.p50,
          p75: percentiles.p75,
          p90: percentiles.p90,
          // Attach raw histogram so aggregation can merge distributions and compute pooled percentiles
          histogramCounts: [...counts],
          bucketSize,
          exchangeRate,
        };
      })
      .filter((p): p is PercentilePointWithHistogram => p !== null);

    return { type: DataType.SINGLE, data: points };
  }

  if (chartType === 'table' && secondaryDimension) {
    const breakdownData: BreakdownSingleDataSet['data'] = (() => {
      if (!data.pay_v3?.entities) return [];

      return data.pay_v3.entities
        .map((entity) => {
          const primaryDimLabel = entity.short_name || entity.long_name;
          if (!primaryDimLabel) return null;

          const topData = selectEntityTopData(entity, secondaryDimension);
          const fieldName = METRIC_FIELD_MAP[metric];

          const breakdown: SingleDataPoint[] = topData.map((item) => {
            const secondaryDimLabel = item.short_name || item.long_name;
            const values = item[fieldName] || [];
            const arr = Array.isArray(values) ? values : [];
            const scaled = (v: number | undefined) =>
              typeof v === 'number' ? v * exchangeRate : null;

            if (
              isPayPercentileMetric(metric) &&
              arr.length >= 5 &&
              typeof arr[0] === 'number' &&
              typeof arr[1] === 'number' &&
              typeof arr[2] === 'number' &&
              typeof arr[3] === 'number' &&
              typeof arr[4] === 'number'
            ) {
              const p10 = scaled(arr[0]);
              const p25 = scaled(arr[1]);
              const p50 = scaled(arr[2]);
              const p75 = scaled(arr[3]);
              const p90 = scaled(arr[4]);
              if (
                p10 == null ||
                p25 == null ||
                p50 == null ||
                p75 == null ||
                p90 == null
              ) {
                return { dim: secondaryDimLabel, value: scaled(arr[2]) };
              }
              return {
                dim: secondaryDimLabel,
                value: p50,
                p10,
                p25,
                p50,
                p75,
                p90,
              } satisfies SingleDataPoint & PercentileDataPoint;
            }

            const medianValue =
              arr.length >= 5 ? (arr[2] ?? null) : (arr[1] ?? null);
            return {
              dim: secondaryDimLabel,
              value:
                typeof medianValue === 'number'
                  ? medianValue * exchangeRate
                  : null,
            };
          });

          return { dim: primaryDimLabel, breakdown };
        })
        .filter(
          (d): d is { dim: string; breakdown: SingleDataPoint[] } => d !== null
        );
    })();

    return { type: DataType.BREAKDOWN_SINGLE, data: breakdownData };
  }

  if (chartType === 'stacked-bar' || chartType === 'grouped-bar') {
    const barData: BreakdownSingleDataSet['data'] = (() => {
      if (!data.pay_v3?.entities) return [];

      return data.pay_v3.entities.map((entity) => {
        const dim = entity.short_name || entity.long_name;
        const topData = selectEntityTopData(entity, secondaryDimension);

        const breakdown: SingleDataPoint[] = topData.map((item) => {
          const secondaryDimLabel = item.short_name || item.long_name;
          const fieldName = METRIC_FIELD_MAP[metric] || metric;
          const values = item[fieldName] || [];
          const medianValue = Array.isArray(values) ? values[1] : null;
          return {
            dim: secondaryDimLabel,
            value:
              typeof medianValue === 'number'
                ? medianValue * exchangeRate
                : null,
            share:
              typeof medianValue === 'number'
                ? medianValue * exchangeRate
                : null,
          };
        });

        return {
          dim,
          breakdown,
        };
      });
    })();
    return { type: DataType.BREAKDOWN_SINGLE, data: barData };
  }

  const barData: SingleDataSet['data'] =
    data.pay_v3?.entities?.map((entity): SingleDataPoint => {
      const dim = entity.short_name || entity.long_name;

      const overtimeData = entity.metrics.overtime;

      const values = (() => {
        switch (metric) {
          case 'base_salary':
          case 'base_salary_percentiles':
            return overtimeData?.salary_base || [];
          case 'total_compensation':
          case 'total_compensation_percentiles':
            return overtimeData?.salary_total || [];
          case 'contractor_pay_rate':
          case 'contractor_pay_rate_percentiles':
            return overtimeData?.salary_hourly || [];
          case 'total_spend_salary':
            return overtimeData?.salary_base || [];
          case 'total_spend_compensation':
            return overtimeData?.salary_total || [];
          default:
            return [];
        }
      })();

      const lastValue = (() => {
        const value = values?.[values.length - 1];
        if (!value) return null;
        if (
          metric === 'total_spend_salary' ||
          metric === 'total_spend_compensation'
        ) {
          const lastCount =
            overtimeData?.counts?.[overtimeData.counts.length - 1];
          if (!lastCount) return null;
          return lastCount * value;
        }
        return value;
      })();
      return {
        id: String(entity.id),
        dim,
        value: typeof lastValue === 'number' ? lastValue * exchangeRate : null,
      };
    }) ?? [];

  return { type: DataType.SINGLE, data: barData };
};
